import { Injectable } from '@angular/core';
import {
  auditTime,
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  finalize,
  map,
  Observable,
  startWith,
} from 'rxjs';
import { shareReplay, switchMap, tap } from 'rxjs/operators';
import {
  IDoFilter,
  IDoFilterOption,
  IDoNetworkFilterItem,
  ILoggerFilters,
} from '@app/main/main-content/display-options/interfaces/do-filter.interface';
import { DoFilterKey } from '@app/main/main-content/display-options/enums/do-filter-key.enum';
import { CorrelationType } from '@app/core/enums/correlation-type.enum';
import { ICorrelation } from '@app/api/interfaces/correlation.interface';
import { IEvent } from '@app/api/interfaces/event.interface';
import { IKmlFile } from '@app/api/interfaces/kml-file.interface';
import { IUiStateDoFilters } from '@app/main/main-content/display-options/interfaces/ui-state-do-filters.interface';
import {
  CorrelationOptionKey,
  DoFilterOptionKey,
  EventFilterRowKey,
  LoggersByAiPredictorOptionKey,
  LoggerTypeOptionKey,
  PipesNetworkSectionItemKey,
} from '@app/main/main-content/display-options/enums/do-filter-option-key.enum';
import { deriveLdEventVisibility } from '@app/main/leak-detection/utils/ld-event-status';
import { LdEventVisibility } from '@app/main/leak-detection/enums/ld-event-visibility.enum';
import {
  applyUiStateToSections,
  getUiStateBoolean,
  setAllSectionRowsChecked,
  syncSelectedFromSections,
} from '@app/main/main-content/display-options/utils/do-filter.utils';
import { getEventFilterLeafKey } from '@app/main/main-content/display-options/utils/do-event-filter.utils';
import equal from 'fast-deep-equal';
import { LeakScoreStatus } from '@app/core/enums/leak-score-status.enum';
import { CommonStateStatus } from '@app/core/enums/common-state-status.enum';
import { ILdLogger } from '@app/main/leak-detection/interfaces/ld-logger';

@Injectable({ providedIn: 'root' })
export class DoFiltersService {
  private _filters$ = new BehaviorSubject<IDoFilter[]>([]);
  private _uiStateFilters$ = new BehaviorSubject<IUiStateDoFilters>({});
  private uiStateFilters$ = this._uiStateFilters$.pipe(distinctUntilChanged(equal));
  private _uiStateOnSave?: (uiStateFilters: IUiStateDoFilters) => void;

  public readonly filters$ = combineLatest([this._filters$, this.uiStateFilters$]).pipe(
    auditTime(0),
    map(([filters, uiState]) => this.combineUiStateAndDoFilters(filters, uiState)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public init(
    defaultFilters: IDoFilter[],
    uiStateSource$?: Observable<IUiStateDoFilters>,
    uiStateOnSave?: (ui: IUiStateDoFilters) => void
  ): Observable<IDoFilter[]> {
    this._uiStateOnSave = uiStateOnSave;
    this._filters$.next(defaultFilters);

    if (uiStateSource$) {
      return uiStateSource$.pipe(
        tap(uiState => this._uiStateFilters$.next(uiState)),
        switchMap(() => this.filters$),
        finalize(() => {
          this.saveDefaultFilters(defaultFilters);
          this.reset();
        })
      );
    } else {
      this._uiStateFilters$.next({});
      return this.filters$.pipe(
        finalize(() => {
          this.saveDefaultFilters(defaultFilters);
          this.reset();
        })
      );
    }
  }

  public setFilters(newFilters: IDoFilter[], changedFilter: IDoFilter | IDoFilterOption) {
    if ('options' in changedFilter) {
      const filter = changedFilter as IDoFilter;

      const prevFilter = this._filters$.getValue().find(item => item.key === filter.key);

      const wasDisabled = !prevFilter?.enabled;
      const nowEnabled = filter.enabled;
      const isSectioned = !!filter.sections?.length;

      if (wasDisabled && nowEnabled) {
        // For a sectioned filter the tree is the source of truth, so re-enabling restores whatever
        // the user had checked rather than blindly selecting everything.
        if (isSectioned) syncSelectedFromSections(filter);

        if (filter.selected.length === 0) {
          if (isSectioned) setAllSectionRowsChecked(filter, true);
          filter.selected = filter.options;
        }
      }

      if (!nowEnabled) {
        filter.selected = [];
      }
    }

    this._filters$.next(newFilters);

    if (changedFilter.uiStateRelated) {
      const uiStateFilters = this.getUiStateFilters(newFilters);
      this._uiStateFilters$.next({ ...this._uiStateFilters$.getValue(), ...uiStateFilters });
      this._uiStateOnSave?.(uiStateFilters);
    }
  }

  public getFiltersCount(): Observable<number> {
    return this.filters$.pipe(
      map(filters => filters.filter(item => item.enabled).length),
      distinctUntilChanged(),
      startWith(0)
    );
  }

  public getLoggerFilters(): Observable<ILoggerFilters> {
    return combineLatest([this.getLoggerTypeFilter(), this.getLoggersByAiPredicatorFilter()]).pipe(
      map(([loggerType, loggersByAi]) => ({ loggerType, loggersByAi }))
    );
  }

  private getLoggerTypeFilter(): Observable<IDoFilter | undefined> {
    return this.filters$.pipe(
      map(filters => filters.find(item => item.key === DoFilterKey.LoggerType)),
      distinctUntilChanged(equal)
    );
  }

  private getLoggersByAiPredicatorFilter(): Observable<IDoFilter | undefined> {
    return this.filters$.pipe(
      map(filters => filters.find(item => item.key === DoFilterKey.LoggersByAIPredictor)),
      distinctUntilChanged(equal)
    );
  }

  public getCorrelationFilter(): Observable<IDoFilter | undefined> {
    return this.filters$.pipe(
      map(filters => filters.find(item => item.key === DoFilterKey.Correlation)),
      distinctUntilChanged(equal)
    );
  }

  public getEventFilter(): Observable<IDoFilter | undefined> {
    return this.filters$.pipe(
      map(filters => filters.find(item => item.key === DoFilterKey.Events)),
      distinctUntilChanged(equal)
    );
  }

  public getPipesNetworkFilter(): Observable<IDoFilter | undefined> {
    return this.filters$.pipe(
      map(filters => filters.find(item => item.key === DoFilterKey.PipesNetwork)),
      distinctUntilChanged(equal)
    );
  }

  public isPipeVisible(pipesNetworkFilter: IDoFilter | undefined): boolean {
    return !!pipesNetworkFilter?.enabled;
  }

  public setKmlFiles(files: IKmlFile[]): void {
    const filters = this._filters$.getValue();
    const pipesNetworkFilter = filters.find(item => item.key === DoFilterKey.PipesNetwork);
    const kmlItem = this.findKmlDisplayItem(pipesNetworkFilter);

    if (!pipesNetworkFilter || !kmlItem) return;

    const previouslyChecked = new Map((kmlItem.children ?? []).map(child => [child.key, child.checked]));

    const updatedKmlItem: IDoNetworkFilterItem = {
      ...kmlItem,
      children: files.map(file => ({
        key: file.name,
        label: file.name,
        rawLabel: true,
        url: file.url,
        checked: previouslyChecked.get(file.name) ?? true,
      })),
    };

    updatedKmlItem.checked = updatedKmlItem.children!.every(child => child.checked);

    const updatedPipesNetworkFilter: IDoFilter = {
      ...pipesNetworkFilter,
      sections: pipesNetworkFilter.sections?.map(section =>
        section.map(item => (item === kmlItem ? updatedKmlItem : item))
      ),
    };

    this._filters$.next(filters.map(filter => (filter === pipesNetworkFilter ? updatedPipesNetworkFilter : filter)));
  }

  public isKmlFileVisible(pipesNetworkFilter: IDoFilter | undefined, kmlFile: IKmlFile): boolean {
    if (!pipesNetworkFilter?.enabled) return false;

    const kmlItem = this.findKmlDisplayItem(pipesNetworkFilter);

    return !!kmlItem?.children?.find(child => child.key === kmlFile.name)?.checked;
  }

  private findKmlDisplayItem(pipesNetworkFilter: IDoFilter | undefined): IDoNetworkFilterItem | undefined {
    return pipesNetworkFilter?.sections?.flat().find(item => item.key === PipesNetworkSectionItemKey.KmlDisplayOnly);
  }

  public isLoggerVisible({ loggerType, loggersByAi }: ILoggerFilters, logger: ILdLogger): boolean {
    if (!loggerType?.enabled && !loggersByAi?.enabled) {
      return false;
    }
    if (loggerType?.enabled) {
      return !!loggerType.selected?.find(filterOption => {
        switch (filterOption.key as LoggerTypeOptionKey) {
          case LoggerTypeOptionKey.Probable:
            return logger.measurement?.leakScoreStatus === LeakScoreStatus.Critical;
          case LoggerTypeOptionKey.Possible:
            return logger.measurement?.leakScoreStatus === LeakScoreStatus.Warn;
          case LoggerTypeOptionKey.NoLeak:
            return logger.measurement?.leakScoreStatus === LeakScoreStatus.Normal;
          case LoggerTypeOptionKey.NoData:
            return !logger.measurement || logger.measurement?.leakScoreStatus === LeakScoreStatus.Neutral;
          default:
            return true;
        }
      });
    }

    if (loggersByAi?.enabled) {
      return !!loggersByAi.selected?.find(filterOption => {
        switch (filterOption.key as LoggersByAiPredictorOptionKey) {
          case LoggersByAiPredictorOptionKey.Probable:
            return logger.measurement?.leakScoreStatus === LeakScoreStatus.Critical;
          case LoggersByAiPredictorOptionKey.Possible:
            return logger.measurement?.leakScoreStatus === LeakScoreStatus.Warn;
          case LoggersByAiPredictorOptionKey.NoLeak:
            return logger.measurement?.leakScoreStatus === LeakScoreStatus.Normal;
          case LoggersByAiPredictorOptionKey.NoData:
            return !logger.measurement || logger.measurement?.leakScoreStatus === LeakScoreStatus.Neutral;
          default:
            return true;
        }
      });
    }

    return true;
  }

  public isCorrelationVisible(filter: IDoFilter | undefined, correlation: ICorrelation): boolean {
    if (!filter?.enabled) {
      return false;
    }

    if (!this.matchesSelectedCorrelationTypes(filter, correlation)) {
      return false;
    }

    return true;
  }

  private matchesSelectedCorrelationTypes(filter: IDoFilter, correlation: ICorrelation): boolean {
    const selectedKeys = new Set((filter.selected ?? []).map(o => o.key as CorrelationOptionKey));

    const typeKeyToCorrelationType: Partial<Record<CorrelationOptionKey, CorrelationType>> = {
      [CorrelationOptionKey.WithPipeInfo]: CorrelationType.Pinpoint,
      [CorrelationOptionKey.WithoutPipeInfo]: CorrelationType.WithoutPipe,
      [CorrelationOptionKey.OutOfBracket]: CorrelationType.OutOfBracket,
      [CorrelationOptionKey.Unsynchronized]: CorrelationType.Unsynced,
    };

    const typeKeys = Object.keys(typeKeyToCorrelationType) as CorrelationOptionKey[];

    const activeTypeKeys = typeKeys.filter(k => selectedKeys.has(k));

    if (!activeTypeKeys.some(k => correlation.correlationType === typeKeyToCorrelationType[k])) {
      return false;
    }

    const qualityKeyToStatus: Partial<Record<CorrelationOptionKey, CommonStateStatus>> = {
      [CorrelationOptionKey.Probable]: CommonStateStatus.Critical,
      [CorrelationOptionKey.Possible]: CommonStateStatus.Warning,
      [CorrelationOptionKey.Suppressed]: CommonStateStatus.Normal,
    };

    const activeQualityKeys = (Object.keys(qualityKeyToStatus) as CorrelationOptionKey[]).filter(k =>
      selectedKeys.has(k)
    );

    return activeQualityKeys.some(k => correlation.qualityStatus === qualityKeyToStatus[k]);
  }

  public isEventVisible(filter: IDoFilter | undefined, event: IEvent): boolean {
    if (!filter?.enabled) return false;

    // Hidden events are only shown while the dedicated row is ticked. That row is not one of the
    // filter's options, so it lives in the tree rather than in `selected`.
    if (deriveLdEventVisibility(event) === LdEventVisibility.Hidden && !this.showHiddenEvents(filter)) return false;

    const leafKey = getEventFilterLeafKey(event);
    if (!leafKey) return false;

    return !!filter.selected?.some(option => option.key === leafKey);
  }

  private showHiddenEvents(filter: IDoFilter): boolean {
    return !!filter.sections?.flat().find(item => item.key === EventFilterRowKey.ShowHiddenEvents)?.checked;
  }

  private combineUiStateAndDoFilters(doFilters: IDoFilter[], uiStateFilters: IUiStateDoFilters): IDoFilter[] {
    return doFilters.map(doFilter => {
      if (!doFilter.uiStateRelated) return doFilter;

      const combined = {
        ...doFilter,
        enabled: getUiStateBoolean(uiStateFilters, doFilter.key),
        selected: doFilter.options.filter(option => getUiStateBoolean(uiStateFilters, option.key)),
      };

      if (combined.sections?.length) applyUiStateToSections(combined, uiStateFilters);

      return combined;
    });
  }

  private getUiStateFilters(doFilters: IDoFilter[]): IUiStateDoFilters {
    return doFilters.reduce<IUiStateDoFilters>((acc, doFilter) => {
      if (doFilter.uiStateRelated) {
        (acc as Record<DoFilterKey, boolean>)[doFilter.key] = doFilter.enabled;

        doFilter.options.forEach(option => {
          if (option.uiStateRelated) {
            (acc as Record<DoFilterOptionKey, boolean>)[option.key] = !!doFilter.selected.find(
              selected => option.key === selected.key
            );
          }
        });
      }

      return acc;
    }, {});
  }

  private saveDefaultFilters(defaultFilters: IDoFilter[]) {
    const currentFilters = this._filters$.getValue();

    const currentFiltersMap = currentFilters.reduce(
      (acc, filter) => {
        acc[filter.key] = filter;

        return acc;
      },
      {} as Record<DoFilterKey, IDoFilter>
    );

    defaultFilters.forEach(filter => {
      const currentFilter = currentFiltersMap[filter.key];

      filter.enabled = currentFilter?.enabled ?? filter.enabled;
      filter.selected = currentFilter?.selected ?? filter.selected;
    });
  }

  private reset() {
    this._uiStateOnSave = undefined;
    this._filters$.next([]);
    this._uiStateFilters$.next({});
  }
}
